import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Footer from "../Footer/Footer";
import Speedbutton from "../SpeedButton/Speedbutton";
import "./PrivacyPolicy.css";

const sections = [
  {
    id: "info-collect",
    icon: "fa-solid fa-database",
    number: "01",
    title: "Information We Collect",
    content: [
      {
        subtitle: "Personal Information",
        text: "When you register for a course, sign up for a newsletter, or fill out a contact form, we may ask for your name, email address, phone number, and professional details.",
      },
      {
        subtitle: "Log Files",
        text: "MSDM follows a standard procedure of using log files. These files log visitors when they visit websites. The information collected includes internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, and referring/exit pages.",
      },
    ],
  },
  {
    id: "how-use",
    icon: "fa-solid fa-gears",
    number: "02",
    title: "How We Use Your Information",
    content: [
      {
        subtitle: null,
        text: "We use the information we collect in various ways, including to:",
      },
    ],
    bullets: [
      "Provide, operate, and maintain our website and services.",
      "Improve, personalize, and expand our website offerings.",
      "Understand and analyze how you use our website.",
      "Communicate with you, either directly or through one of our partners, for customer service or marketing updates.",
      "Process your course enrollments and certifications.",
    ],
  },
  {
    id: "google-ads",
    icon: "fa-brands fa-google",
    number: "03",
    title: "Google Ads & Third-Party Tracking",
    content: [
      {
        subtitle: "Cookies",
        text: 'Our website uses "cookies" to store information including visitors\' preferences and the pages on the website that the visitor accessed.',
      },
      {
        subtitle: "Google DoubleClick DART Cookie",
        text: "Google, as a third-party vendor, uses cookies to serve ads on our site based on your visit to msdm.in and other sites on the internet.",
      },
      {
        subtitle: "Google Consent Mode v2",
        text: "We comply with Google's requirements for user consent. You have the right to accept or decline tracking for advertising and analytics purposes.",
      },
    ],
  },
  {
    id: "data-rights",
    icon: "fa-solid fa-shield-halved",
    number: "04",
    title: "Data Protection Rights",
    content: [
      {
        subtitle: null,
        text: "We would like to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:",
      },
    ],
    bullets: [
      "The right to access – You have the right to request copies of your personal data.",
      "The right to rectification – You have the right to request that we correct any information you believe is inaccurate.",
      "The right to erasure – You have the right to request that we erase your personal data, under certain conditions.",
    ],
  },
  {
    id: "third-party",
    icon: "fa-solid fa-link",
    number: "05",
    title: "Third-Party Privacy Policies",
    content: [
      {
        subtitle: null,
        text: "MSDM's Privacy Policy does not apply to other advertisers or websites. Thus, we are advising you to consult the respective Privacy Policies of these third-party ad servers for more detailed information.",
      },
    ],
  },
  {
    id: "contact",
    icon: "fa-solid fa-headset",
    number: "06",
    title: "Contact Us",
    content: [
      {
        subtitle: null,
        text: "If you have additional questions or require more information about our Privacy Policy, do not hesitate to contact us:",
      },
    ],
    contactItems: [
      { icon: "fa-solid fa-globe", label: "Website", value: "https://msdm.in/", link: "https://msdm.in/" },
      { icon: "fa-solid fa-envelope", label: "Email", value: "businessmsdm@gmail.com", link: "mailto:businessmsdm@gmail.com" },
      { icon: "fa-solid fa-phone", label: "Phone", value: "+91 93632 88733", link: "tel:+919363288733" },
    ],
  },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("info-collect");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);

      const sectionEls = sections.map((s) => document.getElementById(s.id));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 160) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* ───── Hero ───── */}
      <section className="pp-hero">
        <div className="pp-hero__bg-grid" />
        <div className="pp-hero__orb pp-hero__orb--1" />
        <div className="pp-hero__orb pp-hero__orb--2" />
        <div className="pp-hero__orb pp-hero__orb--3" />
        <div className="pp-hero__content">
          <div className="pp-hero__badge">
            <i className="fa-solid fa-lock" />
            <span>Privacy First</span>
          </div>
          <h1 className="pp-hero__title">
            Privacy <span className="pp-gradient-text">Policy</span>
          </h1>
          <p className="pp-hero__subtitle">
            Your trust matters to us. Learn how MSDM collects, uses, and
            protects your information.
          </p>
          <div className="pp-hero__meta">
            <span>
              <i className="fa-regular fa-calendar-check" /> Effective Date:
              April 30, 2026
            </span>
            <span>
              <i className="fa-solid fa-globe" /> msdm.in
            </span>
          </div>
        </div>

        {/* floating stat cards */}
        <div className="pp-hero__stats">
          <div className="pp-stat-card">
            <i className="fa-solid fa-shield-halved" />
            <span>Data Secured</span>
          </div>
          <div className="pp-stat-card">
            <i className="fa-solid fa-user-lock" />
            <span>GDPR Ready</span>
          </div>
          <div className="pp-stat-card">
            <i className="fa-brands fa-google" />
            <span>Consent Mode v2</span>
          </div>
        </div>
      </section>

      {/* ───── Body ───── */}
      <div className="pp-body">
        {/* Sticky sidebar nav */}
        <aside className="pp-sidebar">
          <p className="pp-sidebar__label">Quick Navigation</p>
          <nav className="pp-sidebar__nav">
            {sections.map((s) => (
              <button
                key={s.id}
                className={`pp-sidebar__item ${activeSection === s.id ? "pp-sidebar__item--active" : ""}`}
                onClick={() => scrollToSection(s.id)}
              >
                <i className={s.icon} />
                <span>{s.title}</span>
              </button>
            ))}
          </nav>
          {/* <div className="pp-sidebar__contact-quick">
            <p>Need help?</p>
            <a href="mailto:businessmsdm@gmail.com" className="pp-sidebar__email-btn">
              <i className="fa-solid fa-envelope" /> Email Us
            </a>
          </div> */}
        </aside>

        {/* Main content */}
        <main className="pp-main">
          {sections.map((section, idx) => (
            <article
              key={section.id}
              id={section.id}
              className="pp-card"
              style={{ "--card-delay": `${idx * 0.08}s` }}
            >
              <div className="pp-card__header">
                <div className="pp-card__icon-wrap">
                  <i className={section.icon} />
                </div>
                <div>
                  <span className="pp-card__number">{section.number}</span>
                  <h2 className="pp-card__title">{section.title}</h2>
                </div>
              </div>

              <div className="pp-card__body">
                {section.content.map((item, i) => (
                  <div key={i} className="pp-card__block">
                    {item.subtitle && (
                      <h3 className="pp-card__subtitle">
                        <i className="fa-solid fa-chevron-right" />
                        {item.subtitle}
                      </h3>
                    )}
                    <p className="pp-card__text">{item.text}</p>
                  </div>
                ))}

                {section.bullets && (
                  <ul className="pp-card__list">
                    {section.bullets.map((b, i) => (
                      <li key={i} className="pp-card__list-item">
                        <span className="pp-card__bullet-dot" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}

                {section.contactItems && (
                  <div className="pp-contact-grid">
                    {section.contactItems.map((ci, i) => (
                      <a
                        key={i}
                        href={ci.link}
                        target="_blank"
                        rel="noreferrer"
                        className="pp-contact-item"
                      >
                        <div className="pp-contact-item__icon">
                          <i className={ci.icon} />
                        </div>
                        <div>
                          <span className="pp-contact-item__label">
                            {ci.label}
                          </span>
                          <span className="pp-contact-item__value">
                            {ci.value}
                          </span>
                        </div>
                        <i className="fa-solid fa-arrow-up-right-from-square pp-contact-item__arrow" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}

          {/* Bottom notice */}
          <div className="pp-notice">
            <i className="fa-solid fa-circle-info" />
            <p>
              This Privacy Policy may be updated from time to time. We recommend
              checking this page periodically. Continued use of{" "}
              <a href="https://msdm.in/" target="_blank" rel="noreferrer">
                msdm.in
              </a>{" "}
              constitutes acceptance of any changes.
            </p>
          </div>
        </main>
      </div>

      <Footer />
      <Speedbutton />
    </>
  );
};

export default PrivacyPolicy;